// Supply-drop shop overlay — VARIANT A "Outfitter": the intermission store
// as a proper little shop. Title bar carries SUPPLY DROP left and a SCORE
// wallet chip right (coin icon; the total visibly ticks down on purchase).
// Stock is split into SUPPLIES (instant items) and BOONS (rare/mythical,
// rarity-colored frames) under slim labeled dividers. Each item card is one
// whole-card buy tap target: framed 48px icon shrine with distinct pixel art
// on the left, name + quartermaster flavor line + effect line in the middle,
// price chip (dimmed when unaffordable) + key hint on the right. A purchase
// stamps SOLD diagonally across the card with a 200ms flash; boons with no
// eligible upgrade left read SOLD OUT. Touch, mouse and keyboard (1-4 buy in
// display order, B/Escape leave). Mobile-first single-column card rows at
// 390px, two columns per section at 768px+. While open it holds the sim
// paused via the provided setPaused callback (same pattern as
// UpgradeOverlay).

import type { ShopItemId } from '../sim/shop.js';
import { colors, ensureUiStyles, FONT_UI, icon, pixelShadow } from './theme.js';

/** One stock entry as the overlay renders it (game.ts computes everything). */
export interface ShopEntryView {
  id: ShopItemId;
  icon: string;
  name: string;
  effect: string;
  cost: number;
  /** Wallet covers the price right now. */
  affordable: boolean;
  /** Already bought during this shop opening. */
  sold: boolean;
  /** Boon with no eligible upgrade left (buying would be a refund). */
  soldOut: boolean;
}

export interface ShopOverlayCallbacks {
  /** Called when the player buys an available, affordable entry. */
  onBuy(id: ShopItemId): void;
  /** Called when the player leaves the shop (button, B, or Escape). */
  onLeave(): void;
  /** Pause/resume the simulation while the overlay is open. */
  onSetPaused(paused: boolean): void;
}

const STYLE_ID = 'la-shop-styles';

// Boon cards keep the rarity-frame identity from the upgrade overlay.
const FRAME_COLORS: Partial<Record<ShopItemId, string>> = {
  rare_boon: '#4db8ff',
  mythical_boon: '#c86bff'
};

const BOON_IDS: ReadonlySet<ShopItemId> = new Set(['rare_boon', 'mythical_boon']);

// Distinct pixel art per catalogue entry (public/assets/icons/...). The two
// heart-adjacent consumables split across sets: repair keeps the HUD heart,
// the vitals pod gets the gene-flask; boons draw from the boon set.
const ITEM_ART: Record<ShopItemId, string> = {
  repair: 'assets/icons/hud/heart.png',
  hull: 'assets/icons/boons/hull.png',
  vitals_pod: 'assets/icons/boons/max-hp.png',
  flash_cell: 'assets/icons/boons/dash-charges.png',
  rare_boon: 'assets/icons/boons/crit-chance.png',
  mythical_boon: 'assets/icons/boons/stillpoint-gate.png'
};

// One-line flavor copy per item — in-world, dry quartermaster tone.
const FLAVOR: Record<ShopItemId, string> = {
  repair: 'Patch foam and prayer. Holds, mostly.',
  hull: 'Salvaged plate. The weld marks are free.',
  vitals_pod: 'Gene-bank surplus. Technically yours.',
  flash_cell: 'One more blink per sortie. No questions.',
  rare_boon: 'Off-manifest hardware. No returns.',
  mythical_boon: 'Not standard issue. Sign the waiver.'
};

/** Wallet tick-down duration (ms) after a purchase. */
const WALLET_TICK_MS = 420;

function ensureShopStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* Lumen Abyss supply-drop shop, variant A "Outfitter" — see
   src/ui/shop-overlay.ts. Mobile-first: single-column card rows at 390px,
   two columns per section at 768px. Inset 2px frames (the pause-menu
   language); boon cards carry their rarity color via --la-frame. */
.la-shop-root {
  position: absolute; inset: 0; display: none; box-sizing: border-box;
  align-items: center; justify-content: center;
  background: rgba(5, 10, 20, 0.6);
  z-index: 50;
  font-family: ${FONT_UI}; color: ${colors.text}; font-size: 11px;
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7);
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
           env(safe-area-inset-bottom) env(safe-area-inset-left);
}
.la-shop-panel {
  display: flex; flex-direction: column; align-items: stretch; gap: 12px;
  box-sizing: border-box;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.panelBorder, 4)};
  padding: 16px; width: min(420px, 100%);
  max-width: calc(100vw - 24px);
  max-width: calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right));
  max-height: calc(100vh - 24px);
  max-height: calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  overflow-y: auto; overscroll-behavior: contain; pointer-events: auto;
}
/* Title bar: SUPPLY DROP left, SCORE wallet chip right. */
.la-shop-titlebar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.la-shop-title {
  margin: 0; font-size: 15px; font-weight: 700; letter-spacing: 2px;
  color: ${colors.accent};
  display: flex; align-items: center; gap: 8px;
}
.la-shop-wallet {
  display: inline-flex; align-items: center; gap: 6px; flex: none;
  padding: 4px 8px; box-sizing: border-box;
  background: ${colors.barTrack};
  box-shadow: inset 0 0 0 2px ${colors.accent};
  font-size: 10px; color: ${colors.accent};
}
.la-shop-wallet-label {
  font-size: 8px; letter-spacing: 2px; color: ${colors.muted};
}
.la-shop-wallet-total { font-weight: 700; }
.la-shop-wallet.tick { animation: la-shop-wallet-tick 0.3s ease-out; }
.la-shop-wallet.tick .la-shop-wallet-total { color: ${colors.hp}; }
@keyframes la-shop-wallet-tick {
  40% { transform: scale(1.08); }
}
/* Section: slim labeled divider + card grid. */
.la-shop-section { display: flex; flex-direction: column; gap: 8px; }
.la-shop-divider {
  display: flex; align-items: center; gap: 10px;
  color: ${colors.muted}; font-size: 8px; letter-spacing: 3px;
}
.la-shop-divider::before, .la-shop-divider::after {
  content: ''; flex: 1; height: 2px; background: ${colors.panelBorder}; opacity: 0.7;
}
.la-shop-cards { display: grid; grid-template-columns: 1fr; gap: 8px; }
/* Item card: the WHOLE card is the buy tap target (a real button). */
.la-shop-card {
  position: relative;
  display: grid; align-items: center; text-align: left;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  column-gap: 12px;
  width: 100%; min-height: 56px; padding: 8px 10px; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0;
  /* Only boon cards carry a frame (their rarity color via --la-frame);
     supply cards go frameless — nested rectangles read as noise (A/B judge). */
  box-shadow: inset 0 0 0 2px var(--la-frame, transparent);
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-shop-card:hover:not(:disabled), .la-shop-card:focus-visible {
  filter: brightness(1.25); outline: none;
}
/* Unaffordable: whole card dims — a glowing rarity frame must never read
   as "recommended" when the wallet is short (A/B judge). */
.la-shop-card:disabled { cursor: default; opacity: 0.55; }
/* Entrance stagger runs only on open (the .la-shop-fresh gate) — a
   post-purchase re-render must not replay it. */
.la-shop-panel.la-shop-fresh .la-shop-card {
  opacity: 0; transform: translateY(8px);
  animation: la-shop-card-in 0.25s ease-out forwards;
}
.la-shop-panel.la-shop-fresh .la-shop-card:nth-child(1) { animation-delay: 0.05s; }
.la-shop-panel.la-shop-fresh .la-shop-card:nth-child(2) { animation-delay: 0.1s; }
.la-shop-panel.la-shop-fresh .la-shop-card:nth-child(3) { animation-delay: 0.15s; }
.la-shop-panel.la-shop-fresh .la-shop-card:nth-child(4) { animation-delay: 0.2s; }
@keyframes la-shop-card-in {
  to { opacity: 1; transform: translateY(0); }
}
/* Buy confirmation: a 200ms gold flash across the card. */
.la-shop-card.flash { animation: la-shop-card-flash 0.2s ease-out; }
@keyframes la-shop-card-flash {
  0% { background-color: ${colors.accent}; }
  100% { background-color: ${colors.barTrack}; }
}
/* Bare 64px icon shrine — the art reads on its own, no frame. */
.la-shop-shrine {
  width: 64px; height: 64px; flex: none;
  display: flex; align-items: center; justify-content: center;
}
.la-shop-art {
  width: 64px; height: 64px; display: block;
  image-rendering: pixelated;
}
.la-shop-card-body {
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
}
.la-shop-card-name { font-weight: 700; letter-spacing: 1px; }
.la-shop-card-flavor {
  color: ${colors.muted}; font-size: 8px; line-height: 1.4;
  /* One line, clipped: flavor adds store identity without slowing the scan
     (A/B judge: wrapping flavor bloated the panel). */
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  opacity: 0.8;
}
.la-shop-card-effect { font-size: 9px; line-height: 1.4; }
.la-shop-card-side {
  display: flex; flex-direction: column; align-items: flex-end; gap: 6px;
}
/* Price chip: coin + cost; when the wallet can't cover it the chip turns
   to the red "short" read instead of merely fading (A/B judge). */
.la-shop-price {
  display: inline-flex; align-items: center; gap: 4px; flex: none;
  padding: 4px 8px; box-sizing: border-box;
  background: ${colors.panelBg}; color: ${colors.accent};
  box-shadow: inset 0 0 0 2px ${colors.accent};
  font-size: 10px; font-weight: 700;
}
.la-shop-price.dim {
  color: ${colors.hp};
  box-shadow: inset 0 0 0 2px ${colors.hp};
}
/* Key hints are a keyboard affordance — hidden on touch widths. */
.la-shop-key {
  display: none;
  padding: 2px 6px; font-size: 8px;
  box-shadow: inset 0 0 0 2px ${colors.panelBorder}; color: ${colors.muted};
  min-width: 2ch; text-align: center; box-sizing: border-box;
}
/* Diagonal stamp across a taken card. */
.la-shop-stamp {
  position: absolute; left: 50%; top: 50%; z-index: 1;
  transform: translate(-50%, -50%) rotate(-12deg);
  padding: 4px 12px; pointer-events: none;
  font-size: 12px; font-weight: 700; letter-spacing: 3px;
  color: ${colors.accent};
  background: rgba(7, 21, 38, 0.9);
  box-shadow: inset 0 0 0 2px ${colors.accent};
}
.la-shop-card.soldout .la-shop-stamp {
  color: ${colors.muted}; box-shadow: inset 0 0 0 2px ${colors.muted};
}
.la-shop-card.sold .la-shop-shrine,
.la-shop-card.sold .la-shop-card-body,
.la-shop-card.sold .la-shop-card-side,
.la-shop-card.soldout .la-shop-shrine,
.la-shop-card.soldout .la-shop-card-body,
.la-shop-card.soldout .la-shop-card-side {
  opacity: 0.4;
}
.la-shop-leave {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; min-height: 44px; padding: 8px 12px; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-shop-leave:hover, .la-shop-leave:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
.la-shop-leave .la-shop-key { margin-left: 8px; }
@media (min-width: 768px) {
  .la-shop-root { font-size: 12px; }
  .la-shop-panel { width: min(620px, 100%); gap: 14px; padding: 22px 24px; }
  .la-shop-title { font-size: 18px; }
  .la-shop-wallet { font-size: 11px; }
  .la-shop-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .la-shop-card-flavor, .la-shop-card-effect { font-size: 10px; }
  .la-shop-key { display: inline-block; }
  .la-shop-leave { width: auto; align-self: center; padding: 10px 20px; }
}
@media (min-width: 1600px) {
  .la-shop-root { font-size: 15px; }
  .la-shop-panel { width: min(760px, 100%); }
  .la-shop-title { font-size: 24px; }
  .la-shop-wallet { font-size: 14px; }
  .la-shop-card-flavor, .la-shop-card-effect { font-size: 12px; }
  .la-shop-price { font-size: 12px; }
}
/* Short landscape phones: tighter chrome; the panel scrolls if needed. */
@media (orientation: landscape) and (max-height: 500px) and (max-width: 1599px) {
  .la-shop-panel { gap: 8px; padding: 12px 14px; }
  .la-shop-title { font-size: 13px; }
  .la-shop-cards { gap: 6px; }
  .la-shop-card { min-height: 56px; padding: 6px 8px; grid-template-columns: 44px minmax(0, 1fr) auto; }
  .la-shop-shrine { width: 44px; height: 44px; }
}
`;
  document.head.appendChild(style);
}

export class ShopOverlay {
  private readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly callbacks: ShopOverlayCallbacks;
  private entries: readonly ShopEntryView[] = [];
  /** Entries in DISPLAY order (supplies first, then boons) — keys 1-4 map here. */
  private displayEntries: readonly ShopEntryView[] = [];
  private open = false;
  /** Sold ids as of the last render — a newly sold id triggers the buy flash. */
  private knownSold = new Set<ShopItemId>();
  /** Wallet total as of the last render — a drop triggers the tick-down. */
  private knownWallet: number | null = null;
  private walletRaf: number | null = null;

  constructor(container: HTMLElement, callbacks: ShopOverlayCallbacks) {
    ensureUiStyles();
    ensureShopStyles();
    this.callbacks = callbacks;

    this.root = document.createElement('div');
    this.root.className = 'la-shop-root';
    this.panel = document.createElement('div');
    this.panel.className = 'la-shop-panel';
    this.root.appendChild(this.panel);
    container.appendChild(this.root);
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Open the shop and pause the sim. */
  show(entries: readonly ShopEntryView[], wallet: number): void {
    this.entries = entries;
    // Re-opening the same wave's shop shows prior purchases as SOLD — that
    // state is already known, so no flash / tick on open.
    this.knownSold = new Set(entries.filter((e) => e.sold).map((e) => e.id));
    this.knownWallet = wallet;
    if (!this.open) {
      this.open = true;
      this.callbacks.onSetPaused(true);
      window.addEventListener('keydown', this.onKeyDown);
    }
    this.panel.classList.add('la-shop-fresh');
    this.render(wallet);
    this.root.style.display = 'flex';
  }

  /** Re-render after a purchase (wallet/affordability/SOLD changed). */
  update(entries: readonly ShopEntryView[], wallet: number): void {
    if (!this.open) return;
    this.entries = entries;
    this.panel.classList.remove('la-shop-fresh');
    this.render(wallet);
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    if (this.walletRaf !== null) {
      cancelAnimationFrame(this.walletRaf);
      this.walletRaf = null;
    }
    this.root.style.display = 'none';
    window.removeEventListener('keydown', this.onKeyDown);
    this.callbacks.onSetPaused(false);
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.open) return;
    const n = Number(e.key);
    if (n >= 1 && n <= 4 && Number.isInteger(n)) {
      const entry = this.displayEntries[n - 1];
      if (entry && this.canBuy(entry)) {
        e.preventDefault();
        this.callbacks.onBuy(entry.id);
      }
    } else if (e.key === 'b' || e.key === 'B' || e.key === 'Escape') {
      e.preventDefault();
      this.callbacks.onLeave();
    }
  };

  private canBuy(entry: ShopEntryView): boolean {
    return entry.affordable && !entry.sold && !entry.soldOut;
  }

  private render(wallet: number): void {
    this.panel.textContent = '';

    // Title bar: SUPPLY DROP left, SCORE wallet chip right.
    const titlebar = document.createElement('div');
    titlebar.className = 'la-shop-titlebar';
    const title = document.createElement('h2');
    title.className = 'la-shop-title';
    title.appendChild(icon('shop'));
    title.appendChild(document.createTextNode('SUPPLY DROP'));
    titlebar.appendChild(title);

    const chip = document.createElement('div');
    chip.className = 'la-shop-wallet';
    const label = document.createElement('span');
    label.className = 'la-shop-wallet-label';
    label.textContent = 'SCORE';
    chip.appendChild(label);
    const walletIcon = icon('coin', 14);
    walletIcon.style.color = colors.accent;
    chip.appendChild(walletIcon);
    const walletTotal = document.createElement('span');
    walletTotal.className = 'la-shop-wallet-total';
    chip.appendChild(walletTotal);
    titlebar.appendChild(chip);
    this.panel.appendChild(titlebar);

    // Sections: SUPPLIES (instant items) first, then BOONS — the key hints
    // and keys 1-4 follow this display order.
    const supplies = this.entries.filter((e) => !BOON_IDS.has(e.id));
    const boons = this.entries.filter((e) => BOON_IDS.has(e.id));
    this.displayEntries = [...supplies, ...boons];

    // A card flashes when this render is the first to show it SOLD.
    const flashIds = new Set<ShopItemId>();
    for (const entry of this.entries) {
      if (entry.sold && !this.knownSold.has(entry.id)) flashIds.add(entry.id);
    }

    if (supplies.length > 0) {
      this.panel.appendChild(this.makeSection('SUPPLIES', supplies, 0, flashIds));
    }
    if (boons.length > 0) {
      this.panel.appendChild(this.makeSection('BOONS', boons, supplies.length, flashIds));
    }

    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'la-shop-leave';
    leave.appendChild(icon('arrow-left'));
    leave.appendChild(document.createTextNode('LEAVE'));
    // The [B] key hint is a keyboard affordance — desktop widths only.
    const leaveKey = document.createElement('span');
    leaveKey.className = 'la-shop-key';
    leaveKey.textContent = 'B';
    leave.appendChild(leaveKey);
    leave.addEventListener('click', () => this.callbacks.onLeave());
    this.panel.appendChild(leave);

    // Wallet chip: tick down from the previously shown total on purchase.
    if (this.knownWallet !== null && wallet < this.knownWallet) {
      this.animateWallet(this.knownWallet, wallet, chip, walletTotal);
    } else {
      walletTotal.textContent = wallet.toLocaleString('en-US');
    }
    this.knownWallet = wallet;
    this.knownSold = new Set(this.entries.filter((e) => e.sold).map((e) => e.id));
  }

  private makeSection(
    heading: string,
    entries: readonly ShopEntryView[],
    startIndex: number,
    flashIds: ReadonlySet<ShopItemId>
  ): HTMLElement {
    const section = document.createElement('div');
    section.className = 'la-shop-section';
    const divider = document.createElement('div');
    divider.className = 'la-shop-divider';
    divider.textContent = heading;
    section.appendChild(divider);
    const cards = document.createElement('div');
    cards.className = 'la-shop-cards';
    entries.forEach((entry, i) => {
      cards.appendChild(this.makeCard(entry, startIndex + i, flashIds.has(entry.id)));
    });
    section.appendChild(cards);
    return section;
  }

  private makeCard(entry: ShopEntryView, index: number, flash: boolean): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'la-shop-card';
    const frame = FRAME_COLORS[entry.id];
    if (frame) card.style.setProperty('--la-frame', frame);
    if (entry.sold) card.classList.add('sold');
    if (entry.soldOut) card.classList.add('soldout');
    if (flash) card.classList.add('flash');

    const shrine = document.createElement('span');
    shrine.className = 'la-shop-shrine';
    const art = document.createElement('img');
    art.className = 'la-shop-art';
    art.src = ITEM_ART[entry.id];
    art.alt = '';
    shrine.appendChild(art);
    card.appendChild(shrine);

    const body = document.createElement('span');
    body.className = 'la-shop-card-body';
    const name = document.createElement('span');
    name.className = 'la-shop-card-name';
    name.textContent = entry.name;
    body.appendChild(name);
    const flavor = document.createElement('span');
    flavor.className = 'la-shop-card-flavor';
    flavor.textContent = FLAVOR[entry.id];
    body.appendChild(flavor);
    const effect = document.createElement('span');
    effect.className = 'la-shop-card-effect';
    effect.textContent = entry.effect;
    body.appendChild(effect);
    card.appendChild(body);

    const side = document.createElement('span');
    side.className = 'la-shop-card-side';
    if (!entry.sold && !entry.soldOut) {
      const price = document.createElement('span');
      price.className = entry.affordable ? 'la-shop-price' : 'la-shop-price dim';
      price.appendChild(icon('coin', 12));
      const amount = document.createElement('span');
      amount.textContent = entry.cost.toLocaleString('en-US');
      price.appendChild(amount);
      side.appendChild(price);
      const key = document.createElement('span');
      key.className = 'la-shop-key';
      key.textContent = `${index + 1}`;
      side.appendChild(key);
    }
    card.appendChild(side);

    if (entry.sold || entry.soldOut) {
      const stamp = document.createElement('span');
      stamp.className = 'la-shop-stamp';
      stamp.textContent = entry.sold ? 'SOLD' : 'SOLD OUT';
      card.appendChild(stamp);
      card.setAttribute('aria-label', `${entry.name}, ${stamp.textContent}`);
    }

    card.disabled = !this.canBuy(entry);
    if (!card.disabled) {
      card.addEventListener('click', () => this.callbacks.onBuy(entry.id));
    }
    return card;
  }

  /** Count the wallet chip down from `from` to `to` (eased, ~420ms). */
  private animateWallet(from: number, to: number, chip: HTMLElement, total: HTMLElement): void {
    if (this.walletRaf !== null) cancelAnimationFrame(this.walletRaf);
    chip.classList.add('tick');
    chip.addEventListener('animationend', () => chip.classList.remove('tick'), { once: true });
    const t0 = performance.now();
    const step = (t: number): void => {
      const k = Math.min(1, (t - t0) / WALLET_TICK_MS);
      const eased = 1 - Math.pow(1 - k, 3);
      total.textContent = Math.round(from + (to - from) * eased).toLocaleString('en-US');
      if (k < 1) {
        this.walletRaf = requestAnimationFrame(step);
      } else {
        this.walletRaf = null;
      }
    };
    this.walletRaf = requestAnimationFrame(step);
  }
}
